import { formatDecimal } from '@luminovo/frontend-utils';
import { styled } from '@mui/material';
import { forwardRef } from 'react';

import { colorSystem, h5 } from '../../theme';
import { BaseButton, BaseButtonProps } from '../buttons';
import { Flexbox } from '../Flexbox';

type ButtonProps = Omit<BaseButtonProps, 'href'> & {
    children: React.ReactNode;
    selected: boolean;
    count?: number;
};

const DefaultButton = styled(BaseButton)({
    color: colorSystem.neutral[7],
    // The secondary button's hover, so hovering an option doesn't look like selecting it.
    '&:hover': { background: colorSystem.primary[1], color: colorSystem.primary[7] },
    '&:disabled': { color: colorSystem.neutral[4] },
});

export const ButtonGroupItem = forwardRef(function ButtonGroupItem(
    { children, selected, count, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
): JSX.Element {
    const buttonColor = () => {
        if (selected) {
            return {
                background: colorSystem.neutral[1],
                border: colorSystem.neutral[3],
                color: colorSystem.neutral[9],
                countBackground: colorSystem.neutral[3],
            };
        }

        return {
            border: colorSystem.neutral[3],
            countBackground: colorSystem.neutral[2],
        };
    };

    const { background, border, color, countBackground } = buttonColor();

    const label = (
        <Flexbox
            gap={4}
            sx={{
                alignItems: 'center',
            }}
        >
            {children}
            {count !== undefined ? (
                <div
                    style={{
                        background: countBackground,
                        // neutral[8] on neutral[2] keeps the count readable (6.5:1) when the option isn't selected;
                        // a disabled option's count fades with its label.
                        color: selected || props.disabled ? undefined : colorSystem.neutral[8],
                        borderRadius: '8px',
                        padding: '0px 4px',
                        height: '16px',
                        alignItems: 'center',
                        ...h5,
                    }}
                >
                    {formatDecimal(count)}
                </div>
            ) : null}
        </Flexbox>
    );

    return (
        <DefaultButton
            {...props}
            aria-pressed={selected}
            disableTouchRipple
            ref={ref}
            // On the button, so a selected option's icon shares its text colour, and hover changes neither.
            style={{ border: `1px solid ${border}`, background, color }}
        >
            {label}
        </DefaultButton>
    );
});
